import React, { useEffect, useState } from "react";
import styles from "./CircularGauge.module.scss";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Jauge circulaire (donut SVG) réutilisable.
 *
 * Le pourcentage est TOUJOURS affiché en texte au centre (accessibilité :
 * la valeur ne dépend pas de la seule couleur). Animation du remplissage au
 * montage, désactivée si prefers-reduced-motion.
 *
 * @param value     0–100 (% à remplir)
 * @param color     couleur de l'arc
 * @param size      diamètre px
 * @param stroke    épaisseur de l'arc
 * @param label     libellé sous la jauge
 * @param sublabel  sous-libellé (ex. montant)
 * @param ariaLabel description accessible complète
 */
const CircularGauge = ({
  value,
  color = "#28a745",
  size = 58,
  stroke = 6,
  label,
  sublabel,
  ariaLabel,
}) => {
  const clamped = Math.max(0, Math.min(100, Number(value) || 0));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const targetOffset = circumference * (1 - clamped / 100);

  // Part de l'arc vide au montage → transition CSS vers la cible.
  const [offset, setOffset] = useState(
    prefersReducedMotion() ? targetOffset : circumference,
  );
  useEffect(() => {
    if (prefersReducedMotion()) {
      setOffset(targetOffset);
      return undefined;
    }
    const id = requestAnimationFrame(() => setOffset(targetOffset));
    return () => cancelAnimationFrame(id);
  }, [targetOffset]);

  const center = size / 2;

  return (
    <div className={styles.gauge}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={ariaLabel || `${label || ""} : ${Math.round(clamped)}%`}
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#eef0f2"
          strokeWidth={stroke}
        />
        <circle
          className={styles.arc}
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${center} ${center})`}
        />
        <text
          x="50%"
          y="50%"
          dominantBaseline="central"
          textAnchor="middle"
          className={styles.percent}
          fill={color}
        >
          {Math.round(clamped)}%
        </text>
      </svg>
      {label && <div className={styles.label}>{label}</div>}
      {sublabel && <div className={styles.sublabel}>{sublabel}</div>}
    </div>
  );
};

export default CircularGauge;
